import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Bookmark, Play, Loader2 } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useSavedPosts } from "@/hooks/useSavedPosts";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SavedPostsCard() {
  const [posts, setPosts] = useState(null);
  const [showAll, setShowAll] = useState(false);
  const { savedIds, toggle } = useSavedPosts();

  useEffect(() => {
    axios.get(`${API}/posts/saved`).then((r) => setPosts(r.data || [])).catch(() => setPosts([]));
  }, [savedIds.size]);

  const list = (posts || []).filter((p) => savedIds.has(p.post_id));
  const visible = showAll ? list : list.slice(0, 6);

  return (
    <Card className="p-6" data-testid="saved-posts-card">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-primary/10 text-primary flex items-center justify-center"><Bookmark className="w-5 h-5" /></div>
          <div>
            <h2 className="text-lg font-bold tracking-tight">Salvos</h2>
            <p className="text-xs text-muted-foreground">Publicações que você guardou para ver depois. Só você vê esta lista.</p>
          </div>
        </div>
        <span className="text-xs text-muted-foreground" data-testid="saved-posts-count">{list.length}</span>
      </div>
      {posts === null ? <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" /> : list.length === 0 ? (
        <p className="text-sm text-muted-foreground" data-testid="saved-posts-empty">Nada salvo ainda. Use o menu ⋮ de uma publicação → "Salvar publicação".</p>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="saved-posts-grid">
          {visible.map((p) => {
            const img = p.image_urls?.[0] || p.image_url || p.cover_url;
            return (
              <div key={p.post_id} className="relative group rounded-xl overflow-hidden border border-border bg-muted/40 aspect-square" data-testid={`saved-post-${p.post_id}`}>
                <Link to={`/post/${p.post_id}`} className="block w-full h-full">
                  {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : p.video_url ? (
                    <div className="w-full h-full bg-black flex items-center justify-center"><Play className="w-8 h-8 text-white" /></div>
                  ) : (
                    <div className="w-full h-full p-3 text-xs line-clamp-6 whitespace-pre-wrap">{p.content}</div>
                  )}
                </Link>
                <div className="absolute bottom-0 inset-x-0 p-1.5 bg-gradient-to-t from-black/70 to-transparent text-[10px] text-white truncate">{p.author_name}</div>
                <button onClick={() => toggle(p.post_id)} className="absolute top-1.5 right-1.5 w-7 h-7 rounded-full bg-black/60 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity" title="Remover dos salvos" data-testid={`saved-post-remove-${p.post_id}`}>
                  <Bookmark className="w-3.5 h-3.5 fill-current" />
                </button>
              </div>
            );
          })}
        </div>
      )}
      {list.length > 6 && <Button variant="ghost" size="sm" className="mt-2" onClick={() => setShowAll((s) => !s)} data-testid="saved-posts-toggle">{showAll ? "Ver menos" : `Ver todos (${list.length})`}</Button>}
    </Card>
  );
}
